/**
 * UnpairedScreen — split layout: pairing code on the left, QR on the right.
 *
 * Port of apps/signage-tv/lib/screens/unpaired_screen.dart. The template
 * uses `textContent` for every dynamic string to prevent XSS even in the
 * unlikely case that a backend bug lets untrusted data into the payload.
 */

import { DeviceController } from '../state/controller.js';
import { renderQrSvg } from '../rendering/qr.js';
import { STORAGE_KEYS } from '../storage.js';

export function renderUnpaired(root: HTMLElement, controller: DeviceController): void {
  root.innerHTML = '';
  root.className = 'ag-app ag-unpaired';

  const left = document.createElement('div');
  left.className = 'ag-unpaired__panel';
  root.appendChild(left);

  const kicker = document.createElement('div');
  kicker.className = 'ag-unpaired__kicker';
  kicker.textContent = 'Alora Signage';
  left.appendChild(kicker);

  const title = document.createElement('h1');
  title.className = 'ag-unpaired__title';
  title.textContent = 'Pair this display';
  left.appendChild(title);

  const lede = document.createElement('p');
  lede.className = 'ag-unpaired__lede';
  lede.textContent =
    'Open your admin dashboard, choose "Signage Displays → Pair new display", and enter the code below.';
  left.appendChild(lede);

  const codeEl = document.createElement('div');
  codeEl.className = 'ag-unpaired__code';
  const pairing = controller.pairingCode;
  codeEl.textContent = pairing ? formatCode(pairing.code) : '------';
  left.appendChild(codeEl);

  const meta = document.createElement('div');
  meta.className = 'ag-unpaired__meta';
  if (pairing) {
    meta.textContent = `Code expires ${formatAbsoluteTime(pairing.expires_at)} · Device ${shortDeviceCode(controller)}`;
  } else {
    meta.textContent = 'Requesting a code…';
  }
  left.appendChild(meta);

  // Right panel: QR or placeholder
  const right = document.createElement('div');
  right.className = 'ag-unpaired__qr-wrap';
  root.appendChild(right);

  if (pairing?.claim_url) {
    const card = document.createElement('div');
    card.className = 'ag-unpaired__qr-card';
    // renderQrSvg returns a safe static SVG shape (only numeric coords + fixed
    // colors); assigning via innerHTML here is acceptable.
    card.innerHTML = renderQrSvg(pairing.claim_url);
    right.appendChild(card);

    const label = document.createElement('div');
    label.className = 'ag-unpaired__qr-label';
    label.textContent = 'Scan with your phone to open the admin pairing page';
    right.appendChild(label);
  }

  // Error banner if pairing hit an error
  if (controller.lastError) {
    const banner = document.createElement('div');
    banner.className = 'ag-banner';
    banner.textContent = controller.lastError;
    root.appendChild(banner);
  }
}

function formatCode(code: string): string {
  // Insert a thin space between 3 groups so 6-digit codes read as "428 193"
  if (code.length === 6) {
    return `${code.slice(0, 3)} ${code.slice(3)}`;
  }
  if (code.length === 8) {
    return `${code.slice(0, 4)} ${code.slice(4)}`;
  }
  return code;
}

function formatAbsoluteTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function shortDeviceCode(c: DeviceController): string {
  const raw = c.storage.get(STORAGE_KEYS.deviceCode) ?? '';
  if (raw.length > 10) return raw.slice(0, 10) + '…';
  return raw;
}
